import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, nextTick, computed, useContext } from '@nuxtjs/composition-api';
import { Directions, Positions } from '@/components/common/Dropdown/types';

type DropdownProps = {
  menuClass?: string;
  buttonClass?: string | null;
  autoClose?: boolean;
  fullScreenOnMobile?: boolean;
  isVisible?: boolean;
  controlFromParent?: boolean;
  preventAnimation?: boolean;
  disabled?: boolean;
  position?: Positions;
  direction?: Directions;
  borderless?: boolean;
  /**
   * If you want to disable tabbing to the dropdown menu, set this prop to -1 (standard)
   */
  tabIndex?: number | null;
};
type DropdownEmits = {
  (event: 'toggle', payload: boolean): void;
  (event: 'on-close'): void;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'Dropdown',
  props: {
    menuClass: { default: '' },
    buttonClass: { default: null },
    autoClose: { type: Boolean, default: true },
    fullScreenOnMobile: { type: Boolean, default: false },
    isVisible: { type: Boolean, default: false },
    controlFromParent: { type: Boolean, default: false },
    preventAnimation: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    position: { default: Positions.Left },
    direction: { default: Directions.Bottom },
    borderless: { type: Boolean, default: true },
    tabIndex: { default: null }
  },
  emits: ["toggle", "on-close"],
  setup(__props: any, { emit }: { emit: ({
  (event: 'toggle', payload: boolean): void;
  (event: 'on-close'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { menuClass: string, buttonClass: string | null, autoClose: boolean, fullScreenOnMobile: boolean, isVisible: boolean, controlFromParent: boolean, preventAnimation: boolean, disabled: boolean, position: Positions, direction: Directions, borderless: boolean, tabIndex: number | null };




const DROPDOWN_MENU_LEFT_PADDING = 16;

const { $device } = useContext();

const root = ref<HTMLElement | null>(null);
const opened = ref(props.isVisible);

const menuClassName = computed(() => [
  {
    'dropdown__menu--right': props.position === Positions.Right,
    'dropdown__menu--top': props.direction === Directions.Top,
    'dropdown__menu--full-screen-mobile': props.fullScreenOnMobile && $device.isMobile,
  },
  props.menuClass,
]);

function adjustPosition() {
  nextTick(() => {
    const dropdownMenuElement = root.value?.querySelector('.dropdown__menu') as HTMLElement;
    const parentRect = root.value?.getBoundingClientRect();
    const dropdownMenuElementRect = dropdownMenuElement?.getBoundingClientRect();

    if (props.position === Positions.Right && (dropdownMenuElementRect?.x ?? 0) < 0 && parentRect) {
      Object.assign(dropdownMenuElement?.style, {
        right: 'auto',
        left: `${-parentRect.x + DROPDOWN_MENU_LEFT_PADDING}px`,
      });
    }
  });
}

function handleToggle() {
  if (props.controlFromParent || props.disabled) return;

  opened.value = !opened.value;
}

function handleClose() {
  // close the dropdown only if it's opened
  if (opened.value || props.isVisible) {
    emit('on-close');

    opened.value = false;
  }
}

function onOuterClickOutside(e: MouseEvent) {
  // if click was inside the dropdown menu, do nothing
  if (e.target instanceof Element && e.target.closest('.dropdown__menu')) return;
  // if click was inside the dropdown toggler, do nothing
  if (e.target instanceof Element && e.target.closest('.dropdown__toggler')) return;

  handleClose();
}

function handleMenuClick() {
  if (props.autoClose) handleClose();
}

watch(opened, newValue => {
  if (newValue) {
    adjustPosition();
  }

  emit('toggle', newValue);
});
watch(
  () => props.isVisible,
  newValue => {
    opened.value = newValue;
  },
);

return { __sfc: true,props, emit, DROPDOWN_MENU_LEFT_PADDING, $device, root, opened, menuClassName, adjustPosition, handleToggle, handleClose, onOuterClickOutside, handleMenuClick }
}

})